/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    AfterViewInit,
    Component,
    Input,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import {
    signpostsWithRange,
    templatesConfiguration,
} from 'ng/modules/logs/constants/logs-signpost.constants';

import { ILogsDefaultSignpostData } from 'ng/modules/logs/logs.types';
import { ILogsSignpostStates, LogsSignpostStore } from '../../../../services/logs-signpost.store';
import './logs-signpost-default-view-log-list.component.less';
import * as l10n from './logs-signpost-default-view-log-list.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Signpost Logs list component, displays grid for default template.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-default-view-log-list',
    templateUrl: './logs-signpost-default-view-log-list.component.html',
})
export class LogsSignpostDefaultViewLogListComponent implements AfterViewInit {
    /**
     * Selected signpost config key.
     */
    @Input()
    public configKey: string;

    /**
     * Datagrid template for field name.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for field with range.
     */
    @ViewChild('rangeFieldTemplateRef')
    public readonly rangeFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for field percentage bar.
     */
    @ViewChild('progressBarFieldTemplateRef')
    public readonly progressBarFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for field percentage bar.
     */
    @ViewChild('percentageFieldTemplateRef')
    public readonly percentageFieldTemplateRef: TemplateRef<HTMLElement>;

    public logListGridConfig: IAviDataGridConfig;

    constructor(
        public readonly logsSignpostStore: LogsSignpostStore<ILogsSignpostStates>,
        private readonly l10nService: L10nService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const {
            l10nService,
            configKey,
        } = this;

        this.logListGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'identifier',
                    templateRef: signpostsWithRange.has(configKey) ?
                        this.rangeFieldTemplateRef : this.nameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsNumberLabel),
                    id: 'count',
                    transform: (row: ILogsDefaultSignpostData): number => row.count,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsPercentageLabel),
                    id: 'percentage',
                    transform: (row: ILogsDefaultSignpostData): string => `${row.percentage}%`,
                },
                {
                    label: '',
                    id: 'percentage-bar',
                    templateRef: this.progressBarFieldTemplateRef,
                },
            ],
        };
    }

    /**
     * Callback to ngFor-track by.
     */
    public trackByIndex(index: number): number {
        return index;
    }
}
